import React from "react";

/**
 * Tiny, safe inline markdown formatter for card summaries + briefs.
 *
 * Supports:
 *   ***text***   →  <strong><em>text</em></strong>   (bold italic)
 *   **text**     →  <strong>text</strong>            (bold)
 *   *text*       →  <em>text</em>                    (italic)
 *
 * Design notes:
 *  - Returns React nodes, NOT dangerouslySetInnerHTML — so there's zero XSS
 *    surface even if we ever expose this to non-admin content.
 *  - Leftmost-match semantics: at each step we pick the earliest opening
 *    marker across all three patterns; supports nesting via recursion.
 *  - Non-greedy matches keep short spans intact (e.g. "*foo* and *bar*"
 *    won't collapse into one giant italic).
 *  - Multi-line input is fine — we treat the string opaquely and rely on
 *    the caller to preserve newlines via `whitespace-pre-wrap`.
 */

// Ordered from most-specific → least so the recognizer for triple-star fires
// before the double- or single-star variants on the same substring.
// The bold/bold-italic patterns use a negative-lookahead trick to let their
// interior contain single `*` chars (so "**bold with *italic* inside**" works).
const PATTERNS = [
  {
    re: /\*\*\*((?:(?!\*\*\*).)+?)\*\*\*/,
    wrap: (inner, key) => (
      <strong key={key} className="font-semibold">
        <em>{formatInline(inner)}</em>
      </strong>
    ),
  },
  {
    re: /\*\*((?:(?!\*\*).)+?)\*\*/,
    wrap: (inner, key) => (
      <strong key={key} className="font-semibold">{formatInline(inner)}</strong>
    ),
  },
  {
    re: /\*([^*\s][^*]*?)\*/, // require the opening `*` to be immediately
                              // followed by a non-space non-star char so bullet
                              // points ("* item") don't accidentally italicize.
    wrap: (inner, key) => <em key={key}>{formatInline(inner)}</em>,
  },
];

export function formatInline(text) {
  if (text == null) return null;
  if (typeof text !== "string") return text;

  // Find the leftmost qualifying match across all patterns.
  let best = null;
  for (const p of PATTERNS) {
    const m = text.match(p.re);
    if (!m) continue;
    if (best === null || m.index < best.match.index) {
      best = { match: m, wrap: p.wrap };
    }
  }
  if (!best) return [text];

  const { match, wrap } = best;
  const before = text.slice(0, match.index);
  const after = text.slice(match.index + match[0].length);
  // Unique-enough key to satisfy React list warnings without allocating a uuid.
  const key = `md-${match.index}-${match[0].length}`;
  return [
    before,
    wrap(match[1], key),
    ...formatInline(after),
  ].filter((n) => n !== "" && n !== null && n !== undefined);
}
